/* co-pdp-gallery.js — uneven tile counts in the stacked PDP gallery.

   WHY THIS EXISTS
   The stacked gallery lays product shots two per row with one full-width
   landscape pinned to row 2. That is only tidy when the number of half-width
   tiles is EVEN. An odd count leaves the last tile alone beside a hole.

   That was invisible while every Charlie colour had exactly two tagged shots.
   It appeared the moment the two garden photographs were tagged to Saffron and
   Agave (26 Aug 2026), which took those two colours to three half-width tiles:
     Saffron measured [2, 1, 1] — a lone 402px tile in row 3 beside a hole.

   It is not a Charlie-only problem. This file is written for the SHARED oven
   template, where uneven counts are the norm, not the exception:
     - Cheeky Dark Cherry currently has ONE image
     - Professional will have wildly uneven counts after its photoshoot
   So the rule is: tolerate any count. Never hide photography to make the
   arithmetic work.

   WHY JAVASCRIPT AND NOT :nth-child
   Gang colour filtering hides non-selected media with display:none, and CSS
   counts hidden elements in :nth-child / :nth-last-child. On Charlie 21 of 25
   media are hidden at any moment, so a positional selector picks an arbitrary
   tile. There is no CSS selector for "the last VISIBLE child". Counting has to
   happen at runtime.

   WHY A MutationObserver AND NOT A CHANGE EVENT
   The theme REBUILDS the gallery on variant change rather than toggling
   classes, and it does not emit a documented event when it finishes. Watching
   the list for childList/attribute changes is the only signal that survives
   however the theme chooses to re-render. The observer is disconnected while
   this file mutates classes, so it cannot retrigger itself.

   WIDE TILES ARE DETECTED BY MEASUREMENT, not by filename or alt text, so the
   same logic works for the Charlie (landscape pinned by filename), the Cheeky
   (square crops, different stylesheet) and anything added later. */

(function () {
  'use strict';

  var LIST = '.product__media-list';
  var ORPHAN = 'co-gallery-orphan';
  var WIDE_FRACTION = 0.6; // a tile wider than 60% of the row spans both columns

  function layout(list) {
    var listWidth = list.getBoundingClientRect().width;
    if (!listWidth) return; // not laid out yet (hidden tab, print, etc.)

    var halves = [];
    var i, el, box;

    for (i = 0; i < list.children.length; i++) {
      el = list.children[i];
      el.classList.remove(ORPHAN);

      if (getComputedStyle(el).display === 'none') continue;

      box = el.getBoundingClientRect();
      if (!box.width) continue;
      if (box.width > listWidth * WIDE_FRACTION) continue; // full-width, never pairs

      halves.push(el);
    }

    // An odd number of half-width tiles leaves the last one without a partner.
    // The stylesheet centres it at one column's width rather than letting it
    // sit against a hole. Even counts, and counts of 0, need nothing.
    if (halves.length % 2 === 1) {
      halves[halves.length - 1].classList.add(ORPHAN);
    }
  }

  function init(list) {
    if (list.dataset.coGalleryBound === '1') return;
    list.dataset.coGalleryBound = '1';

    var frame = null;

    var observer = new MutationObserver(function () {
      schedule();
    });

    function connect() {
      observer.observe(list, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'data-gang-option']
      });
    }

    function schedule() {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(function () {
        frame = null;
        observer.disconnect(); // our own class writes must not retrigger us
        try {
          layout(list);
        } finally {
          connect();
        }
      });
    }

    schedule();
    connect();

    // Late-loading images can change box widths after the first pass.
    window.addEventListener('load', schedule, { once: true });
    window.addEventListener('resize', schedule);
  }

  function boot() {
    var lists = document.querySelectorAll(LIST);
    for (var i = 0; i < lists.length; i++) init(lists[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders the whole section.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);
})();
